import { Banner, TabPanel, Text } from '@pega/cosmos-react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { appState } from '../../../interfaces/common';
import {
  resetChatInfo,
  setChatInfo,
  updateCurrentTab,
  updateBannerInfo,
  updateLoaderDetails,
  fetchAllConfigurationRecords,
} from '../../../redux/actions';
import { usePlatformVersionSelector } from '../../../redux/selectors';
import { StyledTabs } from '../../Utilities/custom.styled';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { ShowEncryptionDisableTooltip } from '../../Utilities/ShowEncryptionDisableTooltip';
import { getAccountDetails } from './ChatApis';
import ChannelHooper from './Tabs/ChannelHopper';
import ChatContextData from './Tabs/ContextDataTab';
import StylingTab from './Tabs/StylingTab';
import GeneralTab from './Tabs/GeneralTab';
import GeneralTabSDK from './Tabs/GeneralTabSDK';
import InstallationTab from './Tabs/InstallationTab';
import ProactiveChat from './Tabs/ProactiveChat';
import ProactiveMessagingTab from './Tabs/ProactiveMessagingTab';
import PushNotifications from './Tabs/PushNotification';
import SecurityTab from './Tabs/SecurityTab';
import LanguageTab from './Tabs/LanguageTab/LanguageTab';
import MobileMessaging from './Tabs/MobileMessaging';

const CreateChat: React.FC<any> = (props) => {
  const location = useLocation();
  const { platform: sdkPlatform, id: connectionId } = useParams();
  const {
    sessionId,
    chatInfo,
    chatInfo: { encryptionEnabled },
  } = useSelector((state: appState) => {
    return {
      sessionId: state.sessionId,
      chatInfo: state.chatInfo,
      tabName: state.tabName,
    };
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { fullVersion, majorVersion, minorVersion, patchVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const beyondEightDotSevenDotTwo = fullVersion && fullVersion === 8.7 && patchVersion && patchVersion >= 2;
  const beyondEightDotEight =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 8));
  const isClientPublicKeyPresent = useSelector((state: appState) => state.isClientPublickeyPresent);

  const fetchAccountDetails = useCallback(async () => {
    if (!connectionId) {
      return;
    }
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(setChatInfo(response?.data));
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, dispatch, sessionId, navigate]);

  useEffect(() => {
    if (location.pathname === '/mobile/:platform/:id?' && !(beyondEightDotSevenDotTwo || beyondEightDotEight)) {
      navigate('/');
      return;
    }
    fetchAccountDetails();
    return () => {
      dispatch(resetChatInfo());
    };
  }, [dispatch, beyondEightDotSevenDotTwo, fetchAccountDetails, navigate, location.pathname, beyondEightDotEight]);

  useEffect(() => {
    if (connectionId) {
      dispatch(fetchAllConfigurationRecords());
    }
  }, [connectionId, dispatch]);

  const [panelShown, changePanel] = useState('0');

  const handleTabChange = (id: string) => {
    changePanel(id);
    const currTab = newTabs[parseInt(id, 10)].label;
    dispatch(updateCurrentTab(currTab)); // Update the TabName on change of selection
  };

  const getBaseTabs = useCallback(() => {
    let arr: string[];
    if (!sdkPlatform) {
      arr = ['General', 'Styling', 'Channel switching', 'Context data', 'Security', 'Installation'];
      if (beyondEightDotSeven) {
        arr.push('Proactive chat');
        arr.splice(3, 0, 'Language');
      }
    } else if (sdkPlatform === 'combined_messaging') {
      arr = ['General', 'Styling', 'Context data', 'Security', 'Installation', 'Mobile messaging'];
      if (beyondEightDotSeven) {
        arr.push('Proactive chat');
        arr.splice(3, 0, 'Language');
      }
    } else {
      arr = ['General', 'Context data', 'Security', 'Installation', 'Push notifications'];
      if (beyondEightDotSeven) {
        arr.push('Proactive chat');
      }
    }
    return arr.map((tabName, index) => ({
      name: translate(tabName),
      id: index.toString(),
      disabled: chatInfo?.connectionId || tabName === 'General' ? false : true,
      label: tabName,
    }));
  }, [beyondEightDotSeven, chatInfo, sdkPlatform, translate]);

  const newTabs = getBaseTabs();

  const renderTabContent = (tabName: string) => {
    switch (tabName) {
      case 'General':
        return sdkPlatform === 'sdk_mobile' ? <GeneralTabSDK platform={sdkPlatform} /> : <GeneralTab />;
      case 'Proactive Messaging':
        return <ProactiveMessagingTab />;
      case 'Context data':
        return <ChatContextData platform={sdkPlatform} />;
      case 'Security':
        return <SecurityTab platform={sdkPlatform} />;
      case 'Installation':
        return <InstallationTab platform={sdkPlatform} />;
      case 'Channel switching':
        return <ChannelHooper />;
      case 'Push notifications':
        return <PushNotifications />;
      case 'Proactive chat':
        return <ProactiveChat platform={sdkPlatform} />;
      case 'Styling':
        return <StylingTab />;
      case 'Language':
        return <LanguageTab />;
      case 'Mobile messaging':
        return <MobileMessaging platform={sdkPlatform} />;
      default:
        return '';
    }
  };

  const showEncryptionDisabledBadge = () => {
    return (
      <div>
        <span style={{ float: 'left', marginRight: '20px' }}>{chatInfo?.accountName}</span>
        {beyondEightDotEight && !isClientPublicKeyPresent && encryptionEnabled && <ShowEncryptionDisableTooltip />}
      </div>
    );
  };

  const headerElement = () => {
    const title =
      translate('new') +
      ' ' +
      translate(
        sdkPlatform === 'sdk_mobile'
          ? 'Mobile Messaging SDK'
          : sdkPlatform === 'combined_messaging'
          ? 'combined_messaging'
          : 'Web Messaging',
      );
    return (
      <>
        <Text variant="h1" as="h1">
          {chatInfo?.connectionId ? showEncryptionDisabledBadge() : title}
        </Text>
        {beyondEightDotEight && encryptionEnabled && (
          <Banner
            id="banner-e2ee-chat"
            variant="info"
            messages={[translate('e2ee_info_banner')]}
            style={{ paddingTop: '1rem' }}
          />
        )}
      </>
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      <StyledTabs tabs={newTabs} type="horizontal" onTabClick={handleTabChange} currentTabId={panelShown} />
      <TabPanel tabId={panelShown}>{renderTabContent(newTabs[parseInt(panelShown, 10)].label)}</TabPanel>
    </SectionRenderTemplate>
  );
};

export default CreateChat;
